import { useState } from "react";
import { toast } from "react-toastify";
import { DOCUMENT_TYPES } from "~/config/documents";
import { TOAST_MESSAGES } from "~/constants/messages";
import { GenerateApi } from "~/functions/api/generate";
import { useAppDispatch, useAppSelector } from "~/hooks";
import type { DocumentType } from "~/interface/document";
import type { CommonGenerationBody } from "~/interface/generate";
import { setCurrentProjectOutputsDocumentType, setOrganization } from "~/slices/auth";
import { generateGroupName, joinGroup, leaveGroup } from "~/utils/webPubSubUtils";
import { useWebPubSub } from "../../webPubSubContext";

type GenerateMethod = (
  org_id: string,
  proj_id: string,
  body: CommonGenerationBody,
) => Promise<number | null>;

const GENERATE_METHOD_MAP: Record<string, keyof GenerateApi> = {
  "system-proposal": "generateSystemProposal",
  "functional-requirements": "generateFunctionalRequirements",
  "app-design": "generateAppDesign",
  "field-design": "generateFieldDesign",
  "customize-specification": "generateCustomizeSpecification",
};

const GENERATE_SUCCESS_MESSAGE_MAP: Record<string, string> = {
  "system-proposal": TOAST_MESSAGES.GENERATION.SYSTEM_OVERVIEW_REQUEST_SUCCESS,
  "functional-requirements": TOAST_MESSAGES.GENERATION.FUNC_REQUIREMENT_REQUEST_SUCCESS,
  "app-design": TOAST_MESSAGES.GENERATION.APP_DESIGN_REQUEST_SUCCESS,
  "field-design": TOAST_MESSAGES.GENERATION.FIELD_DESIGN_REQUEST_SUCCESS,
  "customize-specification": TOAST_MESSAGES.GENERATION.CUSTOMIZE_SPEC_REQUEST_SUCCESS,
};

export const useDocumentGeneration = () => {
  const dispatch = useAppDispatch();
  const { currentUser, currentProject, organization } = useAppSelector((state) => state.auth);
  const { client } = useWebPubSub();
  const [isCreditLackModalOpen, setIsCreditLackModalOpen] = useState(false);
  const [lackCredit, setLackCredit] = useState(0);

  const generateDocument = async (documentType: DocumentType) => {
    if (!currentProject) return;
    const documentConfig = DOCUMENT_TYPES[documentType as keyof typeof DOCUMENT_TYPES];
    if (!documentConfig) return;

    const creditCost = documentConfig.creditUsage.generate;
    const documentName = documentConfig.name;

    // クレジットチェック
    if (organization && organization.remainingCredit < creditCost) {
      setLackCredit(creditCost - organization.remainingCredit);
      setIsCreditLackModalOpen(true);
      return;
    }

    const methodName = GENERATE_METHOD_MAP[documentType];
    if (!methodName) return;

    const groupName = generateGroupName(currentProject.id, documentType, "generate");

    try {
      let requestBody: CommonGenerationBody = {};
      if (documentType === "system-proposal") {
        const isRegenerate = !!currentProject?.outputs?.["system-proposal"]?.id;
        requestBody = {
          options: {
            system_proposal: {
              regenerate: isRegenerate,
              mode: "standard",
            },
          },
        };
      }

      // 楽観的に generating 状態へ
      dispatch(
        setCurrentProjectOutputsDocumentType({
          documentType,
          documentId: currentProject?.outputs?.[documentType]?.id || "",
          isGenerating: true,
        }),
      );

      // APIコール前にグループ参加（バックエンドが高速完了してもメッセージを受信できるよう先行参加）
      if (client !== null) {
        await joinGroup(client, groupName);
      }

      const generateApi = new GenerateApi();
      const apiMethod = generateApi[methodName] as GenerateMethod;
      const response = await apiMethod.call(
        generateApi,
        currentUser.organization_id,
        currentProject.id,
        requestBody,
      );

      if (response !== null) {
        // クレジット更新
        if (organization) {
          dispatch(
            setOrganization({
              ...organization,
              remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
            }),
          );
        }
        toast.success(
          GENERATE_SUCCESS_MESSAGE_MAP[documentType] ??
            `${documentName}の生成リクエストに成功しました`,
        );
      } else {
        // 失敗時クリーンアップ
        if (client !== null) leaveGroup(client, groupName);
        dispatch(
          setCurrentProjectOutputsDocumentType({
            documentType,
            documentId: currentProject?.outputs?.[documentType]?.id || "",
            isGenerating: false,
          }),
        );
        toast.error(`${documentName}の生成リクエストに失敗しました`);
      }
    } catch (_error) {
      toast.error(TOAST_MESSAGES.GENERATION.UNKNOWN_ERROR);
      // 失敗時クリーンアップ
      if (client !== null) leaveGroup(client, groupName);
      dispatch(
        setCurrentProjectOutputsDocumentType({
          documentType,
          documentId: currentProject?.outputs?.[documentType]?.id || "",
          isGenerating: false,
        }),
      );
    }
  };

  return {
    generateDocument,
    isCreditLackModalOpen,
    setIsCreditLackModalOpen,
    lackCredit,
  };
};
